import { rowMetrics, sumRows } from '../GscTools/metrics';
import { COVERAGE, formatWindow, hourLabel, selectedHours } from './hours';

/**
 * What the blocks draw, derived from the hours the response produced.
 *
 * The arithmetic is kept here because the one thing this report must not get wrong is *which*
 * hours a number covers, and that decision should be readable in one place rather than inferred
 * from four render methods.
 */

/**
 * Block 03 — every hour Search Console returned, with its metrics as returned.
 *
 * Provisional hours are included and marked. They are kept out of block 02's totals, but this is
 * the evidence table: a reader checking whether a drop is real needs to see the newest hours
 * precisely because they are the ones in question.
 */
export function evidenceRows(hours, hasPosition) {
    return hours
        .filter((hour) => hour.returned)
        .map((hour) => ({
            id: hour.id,
            key: hour.key,
            hour: hour.hour,
            provisional: hour.provisional,
            ...rowMetrics(hour.currentRow, hasPosition),
        }));
}

/**
 * Block 02 — the hours the chart plots, for the selected metric.
 *
 * Each row carries both sides, so the table under the chart is the chart: the two series are read
 * off the same rows the lines are drawn from, and neither can drift from the other.
 */
export function movementRows(hours, { coverage, metric, hasPosition }) {
    return selectedHours(hours, coverage).map((hour) => {
        const current = hour.currentRow ? rowMetrics(hour.currentRow, hasPosition) : null;
        const prior = hour.priorRow ? rowMetrics(hour.priorRow, hasPosition) : null;

        return {
            id: hour.id,
            key: hour.key,
            hour: hour.hour,
            priorHour: hour.priorHour,
            provisional: hour.provisional,
            returned: hour.returned,
            priorReturned: hour.priorReturned,
            comparable: hour.comparable,
            currentValue: current ? current[metric] : null,
            priorValue: prior ? prior[metric] : null,
        };
    });
}

/**
 * The totals the metric cards show: the comparable hours only, on both sides.
 *
 * Always the comparable set, whichever coverage mode the chart is in. A total that silently
 * included the still-collecting hours would be the comparison this report exists to prevent — the
 * newest hours are partial on the current side and complete a week ago, so adding them in makes
 * every window look like a decline. The provisional hours are visible in the chart when the user
 * asks for them; they are never added to a figure that is compared against anything.
 *
 * Both sides are summed from the same hours, so an hour missing on either side is in neither
 * total. CTR comes from summed clicks over summed impressions and position is impression
 * weighted, through the shared totals, rather than either being averaged across hours.
 */
export function comparableTotals(hours, hasPosition) {
    const comparable = hours.filter((hour) => hour.comparable);

    return {
        hours: comparable.length,
        current: sumRows(
            comparable.map((hour) => hour.currentRow),
            hasPosition,
        ),
        prior: sumRows(
            comparable.map((hour) => hour.priorRow),
            hasPosition,
        ),
    };
}

/**
 * Block 01 — the freshness facts, as the table states them side by side.
 *
 * The first incomplete hour is the row that has to be honest about itself. Search Console answers
 * it in `metadata.first_incomplete_hour`, which does not survive the Cloud Function this app
 * reads through, so the value is this report's own assumption and the row says so rather than
 * presenting a derived boundary as a reported one.
 */
export function boundaryRows(t, { scope, window, baselineWindow, hours, baselineReturned }) {
    const hour = (value) =>
        value ? `${hourLabel(value, { withDate: true })} UTC${value.format('Z')}` : null;

    const returned = hours.filter((item) => item.returned);
    const latest = returned.length ? returned[returned.length - 1].hour : null;
    const settled = returned.filter((item) => !item.provisional);
    const cutoff = settled.length ? settled[settled.length - 1].hour : null;
    const notSupplied = t('tools.latest_hours.boundary.not_supplied');

    return [
        {
            id: 'window',
            item: t('tools.latest_hours.boundary.requested'),
            current: formatWindow(window),
            baseline: formatWindow(baselineWindow),
        },
        {
            id: 'latest',
            item: t('tools.latest_hours.boundary.latest'),
            current: hour(latest) ?? t('tools.latest_hours.boundary.none_observed'),
            baseline: baselineReturned
                ? t('tools.latest_hours.boundary.baseline_hours', { hours: baselineReturned })
                : t('tools.latest_hours.boundary.none_observed'),
        },
        {
            id: 'incomplete',
            item: t('tools.latest_hours.boundary.first_incomplete'),
            // Named as an assumption on both sides. The baseline is a week old and every hour in
            // it has long since settled, which is a statement about elapsed time rather than
            // about anything Google told us.
            current: t('tools.latest_hours.boundary.assumed_from', {
                hour: hour(hours.find((item) => item.provisional)?.hour) ?? notSupplied,
            }),
            baseline: t('tools.latest_hours.boundary.baseline_settled'),
        },
        {
            id: 'cutoff',
            item: t('tools.latest_hours.boundary.comparable_cutoff'),
            current: hour(cutoff) ?? t('tools.latest_hours.boundary.none_observed'),
            baseline: cutoff
                ? hour(cutoff.clone().subtract(7, 'days'))
                : t('tools.latest_hours.boundary.none_observed'),
        },
        {
            id: 'source',
            item: t('tools.latest_hours.boundary.source'),
            current: t('tools.latest_hours.boundary.source_value'),
            baseline: scope.comparing
                ? t('tools.latest_hours.boundary.source_value')
                : t('tools.latest_hours.boundary.no_comparison'),
        },
    ];
}

/** Block 04's actions table: what each control on this page does, in one place. */
export function actionRows(t, { scope, exportable }) {
    return [
        {
            id: 'refresh',
            action: t('tools.latest_hours.actions.refresh'),
            behaviour: t('tools.latest_hours.actions.refresh_value'),
        },
        {
            id: 'export',
            action: t('tools.latest_hours.actions.export'),
            behaviour: exportable
                ? t('tools.latest_hours.actions.export_value')
                : t('tools.latest_hours.actions.export_disabled'),
        },
        {
            id: 'coverage',
            action: t('tools.latest_hours.actions.coverage'),
            behaviour:
                scope.coverage === COVERAGE.ALL
                    ? t('tools.latest_hours.actions.coverage_all')
                    : t('tools.latest_hours.actions.coverage_comparable'),
        },
        {
            id: 'availability',
            action: t('tools.latest_hours.actions.availability'),
            behaviour: t('tools.latest_hours.actions.availability_value'),
        },
        {
            id: 'monitoring',
            action: t('tools.latest_hours.actions.monitoring'),
            behaviour: t('tools.latest_hours.actions.monitoring_value'),
        },
    ];
}
